import { createEntityAdapter, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';
import { removeResourceFromProject } from 'store/resources/thunks/removeResourceFromProject.thunk';
import {
  centerOnActiveEmployer,
  centerOnActiveEmployerComplete,
  closeConfirmDeleteOrganizationDialog,
  closeConfirmRemoveEmployeeFromProjectDialog,
  closeModifyOrganizationDialog,
  closeProjectOrganizationView,
  openConfirmDeleteOrganizationDialog,
  openConfirmRemoveEmployeeFromProjectDialog,
  openModifyOrganizationDialog,
  resetOrganizationChartPosition,
  resetOrganizationChartPositionComplete,
  setChartLayoutDirection,
  setOrganizationBusinessIdToAdd,
  setOrganizationBusinessNameToAdd,
  setSearchInputValue,
  setSelectedOrganizationChartMode,
  setShowEmployeeDateOfBirth,
  setShowEmployeeNumber,
  setShowEmployees,
  setShowEmployeeTaxNumber,
  setSubcontractorBusinessAddType,
} from './actions/organizationChart.actions';
import { addProjectOrganizationChartClientForBusiness } from './thunks/addProjectOrganizationChartClientForBusiness.thunk';
import { addSubcontractorExistingBusiness } from './thunks/addSubcontractorExistingBusiness.thunk';
import { addSubcontractorNewBusiness } from './thunks/addSubcontractorNewBusiness.thunk';
import { approveProjectOrganization } from './thunks/approveProjectOrganization.thunk';
import { deleteBusinessOrganizationFromProject } from './thunks/deleteBusinessOrganizationFromProject.thunk';
import { initProjectOrganizationChart } from './thunks/initProjectOrganizationChart.thunk';
import { loadOrganizationChartBusinessInformationByBusinessId } from './thunks/loadBusinessInformationByBusinessId.thunk';
import { loadProjectOrganizationChartItemOfActiveEmployer } from './thunks/loadProjectOrganizationChartItemOfActiveEmployer.thunk';
import { loadProjectOrganizationChartItems } from './thunks/loadProjectOrganizationChartItems.thunk';
import { rejectProjectOrganization } from './thunks/rejectProjectOrganization.thunk';
import { updateOrganizationTilaajavastuuFetchActive } from './thunks/updateOrganizationTilaajavastuuFetchActive.thunk';

const organizationChartItemsAdapter = createEntityAdapter();

export const organizationChartSlice = createSlice({
  name: 'organizationCharts',
  initialState: {
    organizationChartItems: organizationChartItemsAdapter.getInitialState(),
    loading: false,
    isUpdatingOrganizationChart: false,
    isModifyOrganizationDialogOpen: false,
    isConfirmDeleteOrganizationDialogOpen: false,
    isConfirmRemoveEmployeeFromProjectDialogOpen: false,
    selectedResourceToRemove: null,
    modifyOrganizationDialogMode: '',
    modificationTargetId: '',
    modificationSourceId: '',
    shouldResetChart: false,
    shouldCenterOnActiveEmployer: false,
    selectedOrganizationChartMode: 'HIERARCHY',
    selectedChartLayoutDirection: 'TB',
    organizationBusinessIdToAdd: '',
    organizationBusinessNameToAdd: '',
    subcontractorBusinessAddType: 'EXISTING',
    isLoadingBusinessInformation: false,
    isBusinessInformationVerified: false,
    isBusinessInformationSearchedWithoutResult: false,
    searchInputValue: '',
    showEmployees: true,
    showEmployeeOptions: {
      showPhoneNumber: true,
      showTaxNumber: false,
      showDateOfBirth: false,
    },
  },
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(loadProjectOrganizationChartItems.fulfilled, (state, action) => {
        organizationChartItemsAdapter.setMany(
          state.organizationChartItems,
          action.payload
        );
      })
      .addCase(initProjectOrganizationChart.pending, (state) => {
        state.loading = true;
      })
      .addCase(initProjectOrganizationChart.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(initProjectOrganizationChart.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(openModifyOrganizationDialog, (state, action) => {
        const { mode, targetId, sourceId } = action.payload;
        state.isModifyOrganizationDialogOpen = true;
        state.modifyOrganizationDialogMode = mode;
        state.modificationTargetId = targetId;
        state.modificationSourceId = sourceId || '';
      })
      .addCase(closeModifyOrganizationDialog, (state) => {
        state.isModifyOrganizationDialogOpen = false;
        state.modifyOrganizationDialogMode = '';
        state.modificationTargetId = '';
        state.modificationSourceId = '';
        state.organizationBusinessIdToAdd = '';
        state.organizationBusinessNameToAdd = '';
        state.isBusinessInformationVerified = false;
        state.isBusinessInformationSearchedWithoutResult = false;
        state.subcontractorBusinessAddType = 'EXISTING';
      })
      .addCase(openConfirmDeleteOrganizationDialog, (state, action) => {
        const { targetId } = action.payload;

        state.isConfirmDeleteOrganizationDialogOpen = true;
        state.modificationTargetId = targetId;
      })
      .addCase(closeConfirmDeleteOrganizationDialog, (state) => {
        state.isConfirmDeleteOrganizationDialogOpen = false;
        state.modificationTargetId = '';
      })
      .addCase(openConfirmRemoveEmployeeFromProjectDialog, (state, action) => {
        state.isConfirmRemoveEmployeeFromProjectDialogOpen = true;
        state.selectedResourceToRemove = action.payload;
      })
      .addCase(closeConfirmRemoveEmployeeFromProjectDialog, (state) => {
        state.isConfirmRemoveEmployeeFromProjectDialogOpen = false;
        state.selectedResourceToRemove = null;
      })
      .addCase(removeResourceFromProject.fulfilled, (state, action) => {
        state.isConfirmRemoveEmployeeFromProjectDialogOpen = false;
        state.selectedResourceToRemove = null;
      })

      .addCase(
        deleteBusinessOrganizationFromProject.pending,
        (state, action) => {
          state.isUpdatingOrganizationChart = true;
        }
      )
      .addCase(
        deleteBusinessOrganizationFromProject.fulfilled,
        (state, action) => {
          const { deletedOrganizationChartItem } = action.payload;

          organizationChartItemsAdapter.setOne(
            state.organizationChartItems,
            deletedOrganizationChartItem
          );

          state.isUpdatingOrganizationChart = false;
          state.isConfirmDeleteOrganizationDialogOpen = false;
        }
      )
      .addCase(
        deleteBusinessOrganizationFromProject.rejected,
        (state, action) => {
          state.isUpdatingOrganizationChart = false;
        }
      )
      .addCase(resetOrganizationChartPosition, (state) => {
        state.shouldResetChart = true;
      })
      .addCase(resetOrganizationChartPositionComplete, (state) => {
        state.shouldResetChart = false;
      })
      .addCase(centerOnActiveEmployer, (state) => {
        state.shouldCenterOnActiveEmployer = true;
      })
      .addCase(centerOnActiveEmployerComplete, (state) => {
        state.shouldCenterOnActiveEmployer = false;
      })
      .addCase(setChartLayoutDirection, (state, action) => {
        state.selectedChartLayoutDirection = action.payload;
      })
      .addCase(closeProjectOrganizationView, (state, action) => {
        state.selectedChartLayoutDirection = 'TB';
        state.searchInputValue = '';
      })
      .addCase(setOrganizationBusinessIdToAdd, (state, action) => {
        state.organizationBusinessIdToAdd = action.payload;
        state.organizationBusinessNameToAdd = '';
        state.isBusinessInformationVerified = false;
        state.isBusinessInformationSearchedWithoutResult = false;
      })
      .addCase(setOrganizationBusinessNameToAdd, (state, action) => {
        state.organizationBusinessNameToAdd = action.payload;
      })
      .addCase(setSubcontractorBusinessAddType, (state, action) => {
        state.subcontractorBusinessAddType = action.payload;
      })
      .addCase(
        loadOrganizationChartBusinessInformationByBusinessId.pending,
        (state, action) => {
          state.isLoadingBusinessInformation = true;
          state.isBusinessInformationVerified = false;
          state.isBusinessInformationSearchedWithoutResult = false;
        }
      )
      .addCase(
        loadOrganizationChartBusinessInformationByBusinessId.fulfilled,
        (state, action) => {
          const { businessName } = action.payload || {};
          state.isLoadingBusinessInformation = false;

          if (businessName) {
            state.organizationBusinessNameToAdd = businessName;
            state.isBusinessInformationVerified = true;
            state.isBusinessInformationSearchedWithoutResult = false;
          } else {
            state.organizationBusinessNameToAdd = '';
            state.isBusinessInformationVerified = false;
            state.isBusinessInformationSearchedWithoutResult = true;
          }
        }
      )
      .addCase(
        loadOrganizationChartBusinessInformationByBusinessId.rejected,
        (state, action) => {
          state.isLoadingBusinessInformation = false;
          state.isBusinessInformationVerified = false;
          state.isBusinessInformationSearchedWithoutResult = true;
          state.organizationBusinessNameToAdd = '';
        }
      )
      .addCase(setSearchInputValue, (state, action) => {
        state.searchInputValue = action.payload;
      })
      .addCase(approveProjectOrganization.fulfilled, (state, action) => {
        organizationChartItemsAdapter.setOne(
          state.organizationChartItems,
          action.payload
        );
        state.isModifyOrganizationDialogOpen = false;
        state.modifyOrganizationDialogMode = '';
        enqueueSnackbar('Organisaatio hyväksytty projektille', {
          variant: 'success',
        });
      })
      .addCase(approveProjectOrganization.rejected, (state, action) => {
        state.isModifyOrganizationDialogOpen = false;
        state.modifyOrganizationDialogMode = '';
        enqueueSnackbar('Organisaation hyväksyntä epäonnistui', {
          variant: 'error',
        });
      })
      .addCase(rejectProjectOrganization.fulfilled, (state, action) => {
        organizationChartItemsAdapter.setOne(
          state.organizationChartItems,
          action.payload
        );
        state.isModifyOrganizationDialogOpen = false;
        state.modifyOrganizationDialogMode = '';
        enqueueSnackbar('Organisaatio hylätty projektilta', {
          variant: 'success',
        });
      })
      .addCase(rejectProjectOrganization.rejected, (state, action) => {
        state.isModifyOrganizationDialogOpen = false;
        state.modifyOrganizationDialogMode = '';
        enqueueSnackbar('Organisaation hylkäys epäonnistui', {
          variant: 'error',
        });
      })
      .addCase(
        updateOrganizationTilaajavastuuFetchActive.fulfilled,
        (state, action) => {
          organizationChartItemsAdapter.setOne(
            state.organizationChartItems,
            action.payload
          );
        }
      )
      .addCase(
        updateOrganizationTilaajavastuuFetchActive.rejected,
        (state, action) => {
          enqueueSnackbar('Tilaajavastuu-asetuksen päivitys epäonnistui', {
            variant: 'error',
          });
        }
      )
      .addCase(
        loadProjectOrganizationChartItemOfActiveEmployer.fulfilled,
        (state, action) => {
          organizationChartItemsAdapter.addMany(
            state.organizationChartItems,
            action.payload
          );
        }
      )
      .addCase(setSelectedOrganizationChartMode, (state, action) => {
        state.selectedOrganizationChartMode = action.payload;
      })
      .addCase(setShowEmployees, (state, action) => {
        state.showEmployees = action.payload;
      })
      .addCase(setShowEmployeeNumber, (state, action) => {
        state.showEmployeeOptions = {
          ...state.showEmployeeOptions,
          showPhoneNumber: action.payload,
        };
      })
      .addCase(setShowEmployeeTaxNumber, (state, action) => {
        state.showEmployeeOptions = {
          ...state.showEmployeeOptions,
          showTaxNumber: action.payload,
        };
      })
      .addCase(setShowEmployeeDateOfBirth, (state, action) => {
        state.showEmployeeOptions = {
          ...state.showEmployeeOptions,
          showDateOfBirth: action.payload,
        };
      })
      .addMatcher(
        isAnyOf(
          addSubcontractorExistingBusiness.pending,
          addSubcontractorNewBusiness.pending,
          addProjectOrganizationChartClientForBusiness.pending
        ),
        (state, action) => {
          state.isUpdatingOrganizationChart = true;
        }
      )
      .addMatcher(
        isAnyOf(
          addSubcontractorExistingBusiness.fulfilled,
          addSubcontractorNewBusiness.fulfilled,
          addProjectOrganizationChartClientForBusiness.fulfilled
        ),
        (state, action) => {
          organizationChartItemsAdapter.setMany(
            state.organizationChartItems,
            action.payload
          );
          state.isModifyOrganizationDialogOpen = false;
          state.modifyOrganizationDialogMode = '';
          state.modificationTargetId = '';
          state.modificationSourceId = '';
          state.isUpdatingOrganizationChart = false;
          state.organizationBusinessIdToAdd = '';
          state.organizationBusinessNameToAdd = '';
          state.isBusinessInformationVerified = false;
          state.isBusinessInformationSearchedWithoutResult = false;

          enqueueSnackbar('Organisaatiokaavio päivitetty onnistuneesti', {
            variant: 'success',
          });
        }
      )
      .addMatcher(
        isAnyOf(
          addSubcontractorExistingBusiness.rejected,
          addSubcontractorNewBusiness.rejected,
          addProjectOrganizationChartClientForBusiness.rejected
        ),
        (state, action) => {
          state.isUpdatingOrganizationChart = false;
          enqueueSnackbar('Virhe organisaatiokaavion päivityksessä', {
            variant: 'error',
          });
        }
      );
  },
});
